import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { useAccounts } from "@/lib/AccountContext";
import { Plus, X, PiggyBank, Wallet, TrendingUp, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { key: "savings", icon: PiggyBank, i18n: "accounts.type_savings" },
  { key: "checking", icon: Wallet, i18n: "accounts.type_checking" },
  { key: "yield", icon: TrendingUp, i18n: "accounts.type_yield" },
];
const CURRENCIES = ["EUR", "USD", "GBP"];

export function CreateSubaccount() {
  const { t } = useTranslation();
  const { selectedAccount, refreshAccounts, setSelectedAccountId } = useAccounts();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("savings");
  const [label, setLabel] = useState("");
  const [currency, setCurrency] = useState(selectedAccount?.currency || "EUR");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/accounts", {
        account_type: type, label: label.trim() || null, currency,
      });
      toast.success(t("overview.subaccount_created"));
      await refreshAccounts();
      if (data?.id) setSelectedAccountId(data.id);
      setOpen(false); setLabel(""); setType("savings");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="create-subaccount-btn"
        className="group flex items-center gap-3 px-4 py-2.5 rounded-2xl border border-dashed border-blue-300 bg-blue-50/40 hover:bg-blue-50 hover:border-blue-500 transition-all text-left">
        <span className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0"><Plus size={16} /></span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-blue-700">{t("overview.create_subaccount")}</span>
          <span className="block text-[11px] text-blue-500">{t("overview.create_subaccount_desc")}</span>
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={() => setOpen(false)} data-testid="subaccount-modal">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center"><Sparkles className="text-white" size={18} /></div>
                <div>
                  <h3 className="font-display font-bold text-xl text-slate-900">{t("overview.create_subaccount_title")}</h3>
                  <p className="text-xs text-slate-500">{t("overview.create_subaccount_desc")}</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>

            <div className="mt-5">
              <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("users.acc_type")}</span>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {TYPES.map((tp) => {
                  const I = tp.icon; const active = tp.key === type;
                  return (
                    <button key={tp.key} onClick={() => setType(tp.key)} data-testid={`sub-type-${tp.key}`}
                      className={`flex flex-col items-center gap-1.5 py-3 rounded-2xl border transition-all ${active ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:border-blue-300"}`}>
                      <I size={18} />
                      <span className="text-[11px] font-semibold text-center leading-tight px-1">{t(tp.i18n)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="block mt-4">
              <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("overview.subaccount_name")}</span>
              <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("overview.subaccount_name_ph")} data-testid="sub-label"
                className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none" />
            </label>

            <label className="block mt-4">
              <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("transfers.currency")}</span>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="sub-currency"
                className="w-full mt-1.5 px-3 py-2.5 rounded-xl border border-slate-300 text-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="btn-secondary text-sm">{t("users.cancel")}</button>
              <button onClick={submit} disabled={busy} data-testid="sub-create"
                className="inline-flex items-center gap-2 bg-gradient-to-br from-purple-600 to-blue-600 text-white font-semibold px-5 py-2.5 rounded-full text-sm disabled:opacity-50">
                <Plus size={16} /> {busy ? t("overview.subaccount_creating") : t("overview.subaccount_create")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
